// Roster legality (WAIVERS.md "Never into an illegal roster", TRADES.md):
// capacity, position caps, and, only when the league forbids empty slots,
// whether the starting lineup can still be filled. Shared by the waiver and
// trade engines. Pure and sport-free.

/** What "legal roster" means for a league: capacity, position caps, and starter needs. */
export interface RosterShape {
  slots: readonly { id: string; count: number; kind: string; eligible: readonly string[] }[];
  maxPerPosition: Readonly<Record<string, number>>;
  allowEmptySlots: boolean;
}

/** Roster spots that any player may take: starters and bench. Reserve and taxi are separate. */
export function rosterCapacity(shape: RosterShape): number {
  return shape.slots
    .filter((s) => s.kind === 'starter' || s.kind === 'bench')
    .reduce((n, s) => n + s.count, 0);
}

/**
 * Why this roster would be illegal, or null when it is fine. The sentence
 * is the one the button's disabled state and a refused claim both show.
 */
export function rosterProblem(
  roster: readonly string[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
): string | null {
  const cap = rosterCapacity(shape);
  if (roster.length > cap) return `No room: ${cap} of ${cap} roster spots are full.`;
  const byPos = new Map<string, number>();
  for (const id of roster) {
    const pos = positionOf(id);
    byPos.set(pos, (byPos.get(pos) ?? 0) + 1);
  }
  for (const [pos, max] of Object.entries(shape.maxPerPosition)) {
    if ((byPos.get(pos) ?? 0) > max) return `Too many at ${pos}: the limit is ${max}.`;
  }
  if (!shape.allowEmptySlots) {
    const short = starterShortfall(roster, shape, positionOf);
    if (short) return `Short a ${short} for the starting lineup.`;
  }
  return null;
}

/**
 * The first position the starting lineup could not fill from this roster,
 * or null. Dedicated slots are filled first, then flex slots from whatever
 * eligible players remain. Greedy is enough for the shapes leagues use.
 */
export function starterShortfall(
  roster: readonly string[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
): string | null {
  const starters = shape.slots.filter((s) => s.kind === 'starter');
  const remaining = new Map<string, number>();
  for (const id of roster) {
    const pos = positionOf(id);
    remaining.set(pos, (remaining.get(pos) ?? 0) + 1);
  }
  const dedicated = starters.filter((s) => s.eligible.length === 1);
  const flex = starters.filter((s) => s.eligible.length > 1);
  for (const s of dedicated) {
    const pos = s.eligible[0]!;
    const have = remaining.get(pos) ?? 0;
    if (have < s.count) return pos;
    remaining.set(pos, have - s.count);
  }
  for (const s of flex) {
    let need = s.count;
    for (const pos of s.eligible) {
      const have = remaining.get(pos) ?? 0;
      const take = Math.min(have, need);
      remaining.set(pos, have - take);
      need -= take;
      if (need === 0) break;
    }
    if (need > 0) return s.eligible.join('/');
  }
  return null;
}

/**
 * The starting slot a roster leaves open while it has a player with no seat,
 * by the positions it takes, or null when everybody has one. Its starting
 * slots are filled from the players eligible for them as fully as they can be
 * — a matching, so a flex is never spent on a player a dedicated slot needed —
 * and everybody else goes to the bench; when the bench is too small for them,
 * a slot nobody left can play is what kept a seat from them. A roster past its
 * capacity is `rosterProblem`'s, and answers null here.
 *
 * WHY A TRADE ASKS IT (the trade review of 2026-10-22): fifteen players for
 * fifteen spots counts as legal, and a team that traded its only kicker for a
 * receiver onto a full bench had a receiver with no row of the week. The
 * waiver path asks the lineup itself (`WaiverContext.fits`, B-144); a trade
 * is judged before any lineup is read, so it asks the shape.
 */
export function unseatable(
  roster: readonly string[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
): string | null {
  if (roster.length > rosterCapacity(shape)) return null;
  const { short, open } = seating(roster, shape, positionOf);
  return short > 0 ? open : null;
}

/**
 * How many players on a roster would have no seat — its starting slots
 * filled as fully as they can be (`unseatable`'s matching), everybody else on
 * the bench — counting a roster past its capacity too. Zero when everybody
 * has one. What a choice of drops is weighed by (`dropsToFit`).
 */
export function seatsShort(
  roster: readonly string[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
): number {
  return seating(roster, shape, positionOf).short;
}

function seating(
  roster: readonly string[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
): { short: number; open: string | null } {
  const units = shape.slots
    .filter((s) => s.kind === 'starter')
    .flatMap((s) => Array.from({ length: s.count }, () => s));
  const bench = shape.slots.filter((s) => s.kind === 'bench').reduce((n, s) => n + s.count, 0);
  const holder: (number | null)[] = units.map(() => null);
  const fits = (unit: number, player: number) => {
    const s = units[unit]!;
    return s.eligible.length === 0 || s.eligible.includes(positionOf(roster[player]!));
  };
  const seat = (player: number, seen: boolean[]): boolean => {
    for (let u = 0; u < units.length; u += 1) {
      if (seen[u] || !fits(u, player)) continue;
      seen[u] = true;
      const was = holder[u];
      if (was === null || was === undefined || seat(was, seen)) {
        holder[u] = player;
        return true;
      }
    }
    return false;
  };
  let started = 0;
  for (let p = 0; p < roster.length; p += 1)
    if (
      seat(
        p,
        units.map(() => false),
      )
    )
      started += 1;
  const short = Math.max(0, roster.length - started - bench);
  const open = units.find((_, u) => holder[u] === null);
  return { short, open: open === undefined ? null : open.eligible.join('/') };
}

// `positionCount` was here and is gone (2026-09-20). It was exported from
// two package indexes, called by nothing anywhere, and tested by nothing —
// and `rosterProblem` counts positions inline with its own `byPos` map three
// lines above, so it was not even the way this file does the job. Exported
// dead code in a pure package costs a reader the time it takes to work out
// whether it is load-bearing. Six lines, and git has them
// (AUDIT-2026-09-19 F26).
